import { useEffect, useRef, useState, useCallback } from 'react';

export default function useWebSocket(url, token) {
  const wsRef = useRef(null);
  const listenersRef = useRef(new Map());
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    if (!url || !token) return;

    const wsUrl = `${url}?type=dashboard&token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectDelayRef.current = 1000;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const { event: eventName, data } = msg;
          const callbacks = listenersRef.current.get(eventName);
          if (callbacks) {
            callbacks.forEach(cb => cb(data));
          }
        } catch (e) {
          // Ignore invalid messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        // Reconnect with exponential backoff
        const jitter = Math.random() * 1000;
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, Math.min(reconnectDelayRef.current + jitter, 30000));
        reconnectDelayRef.current *= 2;
      };

      ws.onerror = () => {
        // onclose will handle reconnect
      };

      wsRef.current = ws;
    } catch (e) {
      // Will retry via reconnect
    }
  }, [url, token]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const subscribe = useCallback((eventName, callback) => {
    if (!listenersRef.current.has(eventName)) {
      listenersRef.current.set(eventName, new Set());
    }
    listenersRef.current.get(eventName).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = listenersRef.current.get(eventName);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          listenersRef.current.delete(eventName);
        }
      }
    };
  }, []);

  const sendMessage = useCallback((event, data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ event, data, timestamp: new Date().toISOString() }));
    }
  }, []);

  return { isConnected, subscribe, sendMessage };
}
